import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  FaCheck,
  FaBan,
  FaCommentDots,
  FaCrown,
  FaMicrophone,
  FaMicrophoneSlash,
  FaExclamationTriangle,
  FaUserShield,
  FaVideo,
  FaVideoSlash,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { updateParticipantStatus } from '../../store/slices/roomSlice';
import { clearBadgeTypes } from '../../notifications/badges';
import { installMeetingAudioUnlock } from '../../utils/meetingJoinSound';
import { getMeetingDisplayName, getMeetingInitial } from '../../utils/meetingDisplayName';


const toId = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return String(value._id || value.id || value);
};

const normalizeRole = (value) => String(value || '').trim().toLowerCase();

const Participants = ({ socket }) => {
  const dispatch = useDispatch();
  const currentRoom = useSelector((state) => state.room.currentRoom);
  const user = useSelector((state) => state.auth.user);

  useEffect(() => installMeetingAudioUnlock(), []);

  useEffect(() => {
    clearBadgeTypes(['waiting']).catch(() => undefined);
  }, []);



  if (!currentRoom) return null;

  const roomId = currentRoom.roomId;
  const host = currentRoom.host;
  const hostId = toId(host);
  const currentUserId = toId(user);
  const access = currentRoom.access || {};
  const meetingType = currentRoom.meetingType || 'meeting';

  /*
   * Never calculate privileges from role alone in the browser.
   * Backend returns these current-user flags after applying the fixed policy.
   */
  const canAdmit = access.canAdmit === true;
  const canManageMedia = access.canManageMedia === true;

  const coHostIds = new Set((currentRoom.coHosts || []).map(toId));

  const canManageWaitingParticipant = (participant) => {
    if (!canAdmit) return false;

    const targetRole = normalizeRole(participant?.user?.role || participant?.role);

    // Classroom warning/permanent rejection is student-only.  All classroom
    // admin/faculty/staff moderators receive the same server-authorised UI.
    if (meetingType === 'class') {
      return targetRole === 'student';
    }

    // Interview access flags include the selected staff co-hosts.
    return true;
  };

  const handleUpdateStatus = async (
    participantId,
    status,
    { rejectionType = '', reason = '' } = {}
  ) => {
    try {
      const response = await api.put(
        `/rooms/${roomId}/participants/${participantId}/status`,
        { status, rejectionType, reason }
      );

      dispatch(updateParticipantStatus({ participantId, status }));
      toast.success(response.data?.message || `Participant ${status}`);
    } catch (error) {
      console.error('Participant status update failed:', error);
      toast.error(
        error.response?.data?.message ||
          'You do not have permission to update this participant.'
      );
    }
  };

  const emitHostAction = (action, targetUserId = undefined) => {
    if (!socket?.connected) {
      toast.error('Connection lost. Please refresh.');
      return;
    }

    if (!canManageMedia) {
      toast.error('You do not have meeting control access.');
      return;
    }

    socket.emit('host-action', {
      action,
      roomId,
      ...(targetUserId ? { targetUserId } : {}),
    });
  };

  const initiatePrivateChat = (participant) => {
    window.dispatchEvent(
      new CustomEvent('open-private-chat', {
        detail: {
          sessionId: participant.sessionId,
          userId: toId(participant.userId || participant.user),
          username: getMeetingDisplayName(participant),
        },
      })
    );
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-gray-800 text-white">
      <div className="shrink-0 border-b border-gray-700 p-4">
        <h3 className="mb-3 text-lg font-medium">
          Participants ({(currentRoom.participants || []).filter((p) => p.status !== 'left').length})
        </h3>

        {canManageMedia && (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <button
                onClick={() => emitHostAction('mute-all-audio')}
                className="flex-1 rounded bg-red-600 py-1.5 text-[10px] font-semibold hover:bg-red-700"
              >
                <FaMicrophoneSlash className="mr-1 inline" /> Mute All
              </button>
              <button
                onClick={() => emitHostAction('disable-all-video')}
                className="flex-1 rounded bg-red-600 py-1.5 text-[10px] font-semibold hover:bg-red-700"
              >
                <FaVideoSlash className="mr-1 inline" /> Video Off All
              </button>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => emitHostAction('ask-all-unmute')}
                className="flex-1 rounded bg-green-600 py-1.5 text-[10px] font-semibold hover:bg-green-700"
              >
                <FaMicrophone className="mr-1 inline" /> Ask Unmute
              </button>
              <button
                onClick={() => emitHostAction('ask-all-video-on')}
                className="flex-1 rounded bg-green-600 py-1.5 text-[10px] font-semibold hover:bg-green-700"
              >
                <FaVideo className="mr-1 inline" /> Ask Video
              </button>
            </div>
          </div>
        )}
      </div>

      <div
        className="meeting-participant-scroll min-h-0 flex-1 touch-pan-y overflow-x-hidden overflow-y-scroll overscroll-contain pb-4"
        data-testid="meeting-participant-scroll"
        aria-label="Scrollable participant list"
        tabIndex={0}
      >
        {host && (
          <div className="flex items-center justify-between p-3 hover:bg-gray-700">
            <div className="flex min-w-0 flex-1 items-center gap-3">
              <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-yellow-600 font-bold">
                {getMeetingInitial(host, 'H')}
                <span className="absolute -bottom-1 -right-1 rounded-full bg-yellow-500 p-1">
                  <FaCrown className="text-[10px]" />
                </span>
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">
                  {getMeetingDisplayName(host, 'Host')}
                  {hostId === currentUserId && (
                    <span className="ml-1 text-xs text-green-400">(You)</span>
                  )}
                </div>
                <div className="truncate text-xs text-gray-400">
                  Primary Admin Host
                </div>
              </div>
            </div>
          </div>
        )}

        {(currentRoom.participants || [])
          .filter((participant) => {
            const participantUserId = toId(participant.userId || participant.user);
            return participantUserId !== hostId && participant.status !== 'left';
          })
          .map((participant, index) => {
            const participantUserId = toId(participant.userId || participant.user);
            const participantId = participant._id || participant.sessionId;
            const name = getMeetingDisplayName(participant, 'Unknown');
            const currentUser = participantUserId === currentUserId;
            const coHost = coHostIds.has(participantUserId);
            const audioOn = participant.audioEnabled !== false;
            const videoOn = participant.videoEnabled !== false;
            const showLobbyControls =
              participant.status === 'waiting' &&
              !currentUser &&
              canManageWaitingParticipant(participant);
            const targetRole = normalizeRole(
              participant.user?.role || participant.role
            );
            const showStudentRemovalControls =
              meetingType === 'class' &&
              targetRole === 'student' &&
              !currentUser &&
              canAdmit &&
              ['waiting', 'admitted', 'presenting'].includes(participant.status);
            const showWarningRemovalControls =
              !currentUser &&
              access.canReject === true &&
              ['waiting', 'admitted', 'presenting'].includes(participant.status) &&
              (meetingType !== 'class' || targetRole === 'student');

            const warningReject = () => {
              if (!window.confirm(
                `${name}-ஐ warning உடன் remove செய்யவா? அவர் மறுபடியும் waiting request அனுப்பலாம்.`
              )) return;
              void handleUpdateStatus(participantId, 'warning-rejected', {
                rejectionType: 'warning',
                reason: 'Removed from class with a moderator warning',
              });
            };

            const permanentReject = () => {
              if (!window.confirm(
                `${name}-ஐ இந்த Room ID-ல் நிரந்தரமாக block செய்யவா? இந்த செயலை உடனடியாக undo செய்ய முடியாது.`
              )) return;
              const reason = window.prompt('Permanent rejection reason (optional):', '') || '';
              void handleUpdateStatus(participantId, 'permanent-rejected', {
                rejectionType: 'permanent',
                reason,
              });
            };

            return (
              <div
                key={participant.sessionId || participantId || index}
                className="flex items-center justify-between border-t border-gray-700/50 p-3 hover:bg-gray-700"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-500 font-bold">
                    {name.charAt(0).toUpperCase()}
                    {coHost && (
                      <span className="absolute -bottom-1 -right-1 rounded-full bg-blue-400 p-1">
                        <FaUserShield className="text-[10px]" />
                      </span>
                    )}
                  </div>

                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">
                      {name}
                      {currentUser && (
                        <span className="ml-1 text-xs text-green-400">(You)</span>
                      )}
                    </div>
                    <div className="truncate text-[10px] text-gray-400">
                      {participant.status === 'waiting'
                        ? 'Waiting to join'
                        : coHost
                          ? 'Timetable Faculty Co-host'
                          : normalizeRole(participant.user?.role || participant.role) || 'Joined'}
                    </div>
                  </div>
                </div>

                {showLobbyControls && (
                  <div className="ml-2 flex shrink-0 gap-2">
                    <button
                      onClick={() => handleUpdateStatus(participantId, 'admitted')}
                      className="rounded-full bg-green-600 p-2 hover:bg-green-700"
                      title="Admit"
                    >
                      <FaCheck className="text-xs" />
                    </button>
                    {showWarningRemovalControls && (
                      <>
                        <button
                          onClick={warningReject}
                          className="rounded-full bg-amber-500 p-2 hover:bg-amber-600"
                          title="Warning remove — may request again"
                        >
                          <FaExclamationTriangle className="text-xs" />
                        </button>
                        {meetingType === 'class' && showStudentRemovalControls && (
                          <button
                            onClick={permanentReject}
                            className="rounded-full bg-red-700 p-2 hover:bg-red-800"
                            title="Permanent reject for this room"
                          >
                            <FaBan className="text-xs" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}

                {participant.status === 'admitted' && canManageMedia && !currentUser && (
                  <div className="ml-2 flex shrink-0 items-center gap-1">
                    {showWarningRemovalControls && (
                      <>
                        <button
                          onClick={warningReject}
                          className="rounded p-1.5 text-amber-400 hover:bg-gray-600"
                          title="Warning remove — may request again"
                        >
                          <FaExclamationTriangle size={14} />
                        </button>
                        {meetingType === 'class' && showStudentRemovalControls && (
                          <button
                            onClick={permanentReject}
                            className="rounded p-1.5 text-red-500 hover:bg-gray-600"
                            title="Permanent reject from this room"
                          >
                            <FaBan size={14} />
                          </button>
                        )}
                      </>
                    )}
                    <button
                      onClick={() => initiatePrivateChat(participant)}
                      className="rounded p-1.5 text-blue-400 hover:bg-gray-600"
                      title="Private message"
                    >
                      <FaCommentDots size={14} />
                    </button>
                    <button
                      onClick={() =>
                        emitHostAction(
                          audioOn ? 'mute-user-audio' : 'ask-unmute-user',
                          participantUserId
                        )
                      }
                      className="rounded p-1.5 text-red-400 hover:bg-gray-600"
                    >
                      {audioOn ? <FaMicrophoneSlash size={14} /> : <FaMicrophone size={14} />}
                    </button>
                    <button
                      onClick={() =>
                        emitHostAction(
                          videoOn ? 'disable-user-video' : 'ask-video-user',
                          participantUserId
                        )
                      }
                      className="rounded p-1.5 text-red-400 hover:bg-gray-600"
                    >
                      {videoOn ? <FaVideoSlash size={14} /> : <FaVideo size={14} />}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default Participants;
